// DisplayLanguage.js
import React, { useEffect, useState } from "react";
import { deleteUser, getAuth, signOut } from "firebase/auth";
import { Switch } from "@/components/ui/switch";
import { BookOpenText, LifeBuoy } from "lucide-react";
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import InnerContainerLoader from "@/components/InnerContainerLoader";
import nativeLanguageNames from "@/constants/nativeLanguageNames";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useStore from "@/store";
import { initializeUserSettings } from "@/store/settingsStore";
import posthog from "posthog-js";
import PageContainer from "@/components/PageContainer";

// Friendly stand-in avatar, picked consistently per user.
const AVATAR_EMOJIS = ["🦊", "🐼", "🐨", "🦉", "🐸", "🐙", "🦄", "🐯", "🐰", "🐧"];
const avatarEmojiFor = (seed = "") => {
  let hash = 0;
  for (const char of seed) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return AVATAR_EMOJIS[hash % AVATAR_EMOJIS.length];
};

const Account = () => {
  const { user } = useStore().user;
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const {
    settings,
    updateSettings,
    toggleSoundEffects,
    soundEffects,
  } = useStore().settings;

  const [isInitialized, setIsInitialized] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);

  useEffect(() => {
    const initialiseSettings = async () => {
      try {
        await initializeUserSettings();
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize user settings:", error);
      }
    };
    initialiseSettings();
  }, []);

  const handleLanguageChange = (newLanguage) => {
    // Update the native_language setting
    updateSettings({ native_language: newLanguage });
  };

  useEffect(() => {
    posthog.capture("settings_page_viewed");
  }, []);

  const accuracyMode = settings?.accuracy_mode || "easy";
  const nativeLanguageValue = settings?.native_language;

  const handleAccuracyModeChange = (mode) => {
    if (mode === accuracyMode) {
      return;
    }
    updateSettings({ accuracy_mode: mode });
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        posthog.capture("user_logged_out");
      })
      .catch(() => {});
  };

  const handleAccountDeletion = () => {
    if (!currentUser) {
      return;
    }

    if (
      typeof window !== "undefined" &&
      !window.confirm(
        "Are you sure you want to delete your account? This action cannot be undone."
      )
    ) {
      return;
    }

    deleteUser(currentUser)
      .then(() => {
        posthog.capture("user_account_deleted");
        signOut(auth);
      })
      .catch((error) => {
        if (error?.code === "auth/requires-recent-login") {
          alert("Please log in again before deleting your account.");
        } else {
          alert("Unable to delete this account right now. Please try again.");
        }
      });
  };

  const formatDate = (timestamp) => {
    const date = new Date(parseInt(timestamp));
    const options = { year: "numeric", month: "long", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  if (!isInitialized || !user) {
    return (
      <PageContainer innerClassName="items-center justify-center">
        <InnerContainerLoader offset={12} />
      </PageContainer>
    );
  }

  return (
    <PageContainer innerClassName="max-w-5xl gap-8">
      <header className="flex flex-col gap-4">
        <span className="inline-flex w-fit items-center gap-2 rounded-full border border-line bg-surface/80 px-4 py-1 text-[10px] uppercase tracking-[0.4em] text-primary/80">
          Account
        </span>
        <div className="space-y-3">
          <h1 className="text-4xl font-semibold tracking-tight text-fg sm:text-5xl">
            Account
          </h1>
          <p className="max-w-2xl text-sm text-fg-muted">
            Manage who you are on SpeechCraft and fine-tune the preferences that
            keep practice feeling effortless.
          </p>
        </div>
      </header>

      <section className="rounded-3xl p-7">
        {currentUser ? (
          <>
            <div className="flex flex-col items-start gap-7 sm:flex-row sm:items-center">
              <div className="flex h-[6.25rem] w-[6.25rem] items-center justify-center overflow-hidden rounded-2xl border border-line bg-canvas/30 shadow-inner">
                {currentUser.photoURL && !photoFailed ? (
                  <img
                    className="h-full w-full object-cover"
                    src={currentUser.photoURL}
                    alt="Profile"
                    // Google profile photos often refuse requests that send a referrer.
                    referrerPolicy="no-referrer"
                    onError={() => setPhotoFailed(true)}
                  />
                ) : (
                  <span className="text-5xl" role="img" aria-label="Profile">
                    {avatarEmojiFor(currentUser.email || currentUser.uid)}
                  </span>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <h2 className="text-3xl font-semibold text-fg">
                    {currentUser.displayName || "Unnamed learner"}
                  </h2>
                  <p className="text-[1.09375rem] text-fg-muted">
                    {currentUser.email}
                  </p>
                </div>
                <div className="text-[0.9375rem] uppercase tracking-[0.35em] text-fg-muted">
                  Joined {formatDate(currentUser.metadata.createdAt)}
                </div>
              </div>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                onClick={handleLogout}
                className="rounded-full border border-accent/45 bg-accent/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.3em] text-accent transition hover:border-accent/65 hover:bg-accent/18"
              >
                Log out
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleAccountDeletion}
                className="rounded-full border border-line bg-transparent px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.3em] text-fg transition hover:border-primary/40 hover:bg-elevated"
              >
                Delete account
              </Button>
            </div>
          </>
        ) : (
          <div className="rounded-3xl border border-line bg-surface/80 px-6 py-8 text-sm text-fg shadow-panel">
            Unable to load profile right now.
          </div>
        )}
      </section>

      <section className="rounded-3xl border border-line bg-surface/80 p-6 shadow-panel backdrop-blur-xl">
        <div className="space-y-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-[0.3em] text-fg">
                Native language
              </h2>
              <p className="text-xs text-fg-muted">
                Used for translating each word and for pronunciation feedback.
              </p>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-[200px] justify-between rounded-2xl border border-line bg-elevated px-4 py-2 text-sm font-semibold text-fg shadow-panel hover:border-primary/40 hover:bg-elevated"
                >
                  {nativeLanguageNames[nativeLanguageValue] ||
                    "Select language..."}
                  <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] rounded-3xl border border-line bg-surface p-0 text-fg shadow-panel backdrop-blur-2xl">
                <Command>
                  <CommandInput
                    placeholder="Search language..."
                    className="h-9"
                  />
                  <CommandEmpty>No language found.</CommandEmpty>
                  <CommandGroup className="max-h-[50vh] overflow-y-auto">
                    {Object.keys(nativeLanguageNames).map((languageCode) => (
                      <CommandItem
                        key={languageCode}
                        value={nativeLanguageNames[languageCode]}
                        onSelect={() => {
                          handleLanguageChange(
                            languageCode.replace(/_/g, "-")
                          );
                        }}
                      >
                        {nativeLanguageNames[languageCode]}
                        <CheckIcon
                          className={cn(
                            "ml-auto h-4 w-4",
                            nativeLanguageValue ===
                              languageCode.replace(/_/g, "-")
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-[0.3em] text-fg">
                Sound effects
              </h2>
              <p className="text-xs text-fg-muted">
                Play gentle audio cues during practice sessions.
              </p>
            </div>
            <Switch
              checked={soundEffects}
              onCheckedChange={toggleSoundEffects}
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-[0.3em] text-fg">
                Choose your mode
              </h2>
              <p className="text-xs text-fg-muted">
                Easy gives partial credit for close sounds and chimes at 80%. Hard
                scores strictly, with no credit for near-misses, and chimes at 100%.
              </p>
            </div>
            <div className="inline-flex items-center rounded-full border border-line bg-surface/80 p-1 shadow-panel">
              {[
                { id: "easy", label: "Easy" },
                { id: "hard", label: "Hard" },
              ].map((option) => {
                const isActive = accuracyMode === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleAccuracyModeChange(option.id)}
                    className={`relative flex items-center gap-2 rounded-full px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] transition ${
                      isActive
                        ? "bg-primary/80 text-primary-fg shadow-panel"
                        : "text-fg-muted hover:text-fg"
                    }`}
                    aria-pressed={isActive}
                  >
                    <span
                      className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border text-[10px] transition ${
                        isActive
                          ? "border-primary/30 bg-primary/10 text-primary-fg"
                          : "border-transparent text-transparent"
                      }`}
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                    <span className="whitespace-nowrap">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-line bg-surface/80 p-6 shadow-panel backdrop-blur-xl">
        <h3 className="text-lg font-semibold text-fg">Help &amp; support</h3>
        <p className="mt-1 text-sm text-fg-muted">
          Stuck, found a bug, or have an idea? We read every message.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <a
            href="mailto:hello@langcraft.world"
            className="inline-flex items-center gap-2 rounded-lg border border-primary/60 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary transition hover:bg-primary/20"
          >
            <LifeBuoy size={16} strokeWidth={1.8} />
            Email support
          </a>
          <a
            href="https://speechcraft.io/docs/intro/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-line px-4 py-2 text-sm font-semibold text-fg transition hover:border-primary/50 hover:text-primary"
          >
            <BookOpenText size={16} strokeWidth={1.8} />
            Read the docs
          </a>
        </div>
        <p className="mt-3 text-xs text-fg-subtle">hello@langcraft.world</p>
      </section>

      <div className="mt-4 flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.35em] text-primary/80">
        Free forever
        <span className="animate-pulse text-lg text-danger">♥</span>
      </div>
    </PageContainer>
  );
};

export default Account;
